/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import React, { FunctionComponent, ReactElement } from "react";
import { Stack, IStackTokens } from "@fluentui/react";
import { Dialog, DialogType, DialogFooter } from "@fluentui/react/lib/Dialog";
import { TextField } from "@fluentui/react/lib/TextField";
import { ChoiceGroup, IChoiceGroupOption } from "@fluentui/react/lib/ChoiceGroup";
import { useId } from "@fluentui/react-hooks";
import { PrimaryButton, DefaultButton } from "@fluentui/react/lib/Button";
import { useTranslation } from "react-i18next";
import {
    CreateNewDefinitionCommands,
    ICreateNewDefinitionState
} from "./CreateNewDefinitionViewModel";
import { IStartViewViewModel } from "./StartViewViewModel";
import {
    EvaluationStrategy,
    ISettingGroupDefinition,
    SettingGroupDefinition
} from "@nuance/hierarchical-settings-ui-domain";
import createCommands from "../CreateCommands";

const createNewDefinitionCommands = createCommands<CreateNewDefinitionCommands>(
    CreateNewDefinitionCommands
);
const cmds = createNewDefinitionCommands.Cmds;

const initialState: ICreateNewDefinitionState = {
    canSave: false,
    evaluationMethod: EvaluationStrategy[EvaluationStrategy.Classic],
    settingDefinitionName: "",
    settingDefinitionErrorMsg: ""
};

/**
 * Interface for props to New Definition Dialog.
 */
interface ICreateNewDefinitionProps {
    startViewVM: IStartViewViewModel;
}

/**
 * New definition dialog component.
 */
const CreateNewDefinition: FunctionComponent<ICreateNewDefinitionProps> = (props): ReactElement => {
    const [state, dispatch] = React.useReducer(createNewDefinitionCommands.Reducer, initialState);

    //const vm = { state, cmds, dispatch };

    const { t } = useTranslation();
    const labelId: string = useId("dialogLabel");
    const subTextId: string = useId("subTextLabel");
    const dialogContentProps = {
        type: DialogType.normal,
        title: t("CreateNew"),
        closeButtonAriaLabel: "Close", // translation here?
        subText: t("CreateNewDefinitionDescription")
    };
    const modalProps = React.useMemo(
        () => ({
            titleAriaId: labelId,
            subtitleAriaId: subTextId,
            isBlocking: true
        }),
        [labelId, subTextId]
    );
    const options: IChoiceGroupOption[] = [
        {
            key: EvaluationStrategy[EvaluationStrategy.Classic],
            text: t("ClassicHierarchy"),
            ariaLabel: t("ClassicHierarchy")
        },
        {
            key: EvaluationStrategy[EvaluationStrategy.OrgGroups],
            text: t("OrgGroupHierarchy"),
            ariaLabel: t("OrgGroupHierarchy")
        },
        {
            key: EvaluationStrategy[EvaluationStrategy.NonHierarchical],
            text: t("NonHierarchical"),
            ariaLabel: t("NonHierarchical")
        }
    ];
    const stackTokens: IStackTokens = { childrenGap: 20 };

    const onDefinitionNameChange = (
        _: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string
    ) => {
        if (newValue !== undefined) {
            dispatch(cmds.definitionNameChanged(newValue));
        }
    };

    const onEvaluationChange = (
        _?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined,
        option?: IChoiceGroupOption | undefined
    ) => {
        if (option) {
            dispatch(cmds.evaluationMethodChange(option.key));
        }
    };

    const onNameBlur = () => {
        dispatch(cmds.nameBlur());
    };

    const onSave = () => {
        const evalMethod: string = state.evaluationMethod;

        const definition = new SettingGroupDefinition(state.settingDefinitionName);
        definition.EvaluationStrategy =
            EvaluationStrategy[evalMethod as keyof typeof EvaluationStrategy];

        const def: ISettingGroupDefinition = JSON.parse(JSON.stringify(definition));

        props.startViewVM.state.parentVM.dispatch(
            props.startViewVM.state.parentVM.cmds.openMainView(def)
        );
    };

    const onCancel = () => {
        props.startViewVM.dispatch(props.startViewVM.cmds.cancelCreateDefinitionDialog());
    };

    return (
        <Dialog
            hidden={!props.startViewVM.state.showCreateDefDialog}
            onDismiss={onCancel}
            minWidth={450}
            dialogContentProps={dialogContentProps}
            modalProps={modalProps}
        >
            <Stack tokens={stackTokens}>
                <TextField
                    label={t("SettingsDefinitionName")}
                    onChange={onDefinitionNameChange}
                    onBlur={onNameBlur}
                    required
                    maxLength={50}
                    errorMessage={t(state.settingDefinitionErrorMsg)}
                    role="TextField"
                />
                <ChoiceGroup
                    defaultSelectedKey={EvaluationStrategy[EvaluationStrategy.Classic]}
                    options={options}
                    label={t("EvaluationMethod")}
                    onChange={onEvaluationChange}
                    role="ChoiceGroup"
                />
            </Stack>
            <DialogFooter>
                <PrimaryButton onClick={onSave} text={t("Save")} disabled={!state.canSave} />
                <DefaultButton onClick={onCancel} text={t("Cancel")} />
            </DialogFooter>
        </Dialog>
    );
};

export default CreateNewDefinition;
